import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router'
import { LanguageContext, ReactState } from '../../../../../React/Common/Contexts'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { PasswordChangeLanguage } from '../../../../../../i18n'
import { PasswordChangeFormType } from '../Types/Types'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { FormProvider, useForm, UseFormMethods } from '../../../../../Vendor/react-hook-form/dist/index.ie11'
import { ON_CHANGE } from '../../../../../React/Common/Constants/HookFormEventNames'
import { useConfig } from '../../../../../React/Common/Hooks/useConfig'
import { DEFAULT_PASSWORD_MIN_LENGTH, PASSWORD_POLICY_CONFIG, SERVLET_COMMANDS_TO_ROUTE } from '../Constants/Constants'
import { getSession } from '../../../../../React/Common/API/CommonAPI'
import { SessionAPIResponse } from '../../../../../NonReact/Types/Types'
import {
    APIElements,
    changeCurrentUserPasswordURL,
    RESET_PASSWORD_URL
} from '../../../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../../../NonReact/constants/REST'
import { encrypt } from '../../../../../React/Common/Utils/Security'
import { requestJSON } from '../../../../../NonReact/common/API/REST/RESTClient'
import { isStringNotEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { displayError } from '../../../../../React/Common/Utils/Messages'
import Loader from '../../../../../React/Common/Components/Loader'
import { PasswordChangeForm } from '../Form/PasswordChangeForm'
import { FooterActions } from '../Actions/FooterActions'
import { AUTH_ROUTE_PATH } from '../../../../Constants/Constants'
import { getQueryParam } from '../../../../../NonReact/utils/LocationUtils'
import { LanguageUtils } from '../../../../../React/Common/Utils/LanguageUtils'
import { RoundedButton } from '../../../Atoms/RoundedButton/RoundedButton'
import { UserInfo, useUserInfo } from '../../../../Hooks/useUserInfo'
import { APIData } from '../../../../../NonReact/common/Types/Types'
import { performAuthOutflowRouting } from '../../../../Utilities/RoutingUtils'
import { redirectToServletWithBranding } from '../../../../../NonReact/constants/ServletCommands'
import { Branding } from '../../../../../React/Common/API/BrandingAPI'
import { useBranding } from '../../../../../React/Common/Hooks/useBranding'

const CMD: string = 'CMD_'

export const PasswordChange: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(PasswordChangeLanguage.NAME)

    const location: Location = useLocation()
    const navigate: NavigateFunction = useNavigate()

    const [userInfo]: UserInfo = useUserInfo(true)

    const branding: Nullable<Branding> = useBranding()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const defaultValues: MutableRefObject<PasswordChangeFormType> = useRef({
        oldPassword: EMPTY_STRING,
        newPassword: EMPTY_STRING,
        confirmPassword: EMPTY_STRING
    })

    const methods: UseFormMethods<PasswordChangeFormType> = useForm<PasswordChangeFormType>({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const isAdmin: boolean = useMemo((): boolean => (userInfo?.Permissions?.Admin?.length ?? 0) > 0, [userInfo])

    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const accountResetIDState: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [accountResetID, setAccountResetID]: ReactState<Nullable<string>> = accountResetIDState
    const [isPasswordResetSuccess, setIsPasswordResetSuccess]: ReactState<boolean> = useState<boolean>(false)

    const minPasswordLengthAdmin: number = useConfig(
        PASSWORD_POLICY_CONFIG,
        'adminUser.minLength',
        DEFAULT_PASSWORD_MIN_LENGTH
    )
    const minPasswordLengthReporting: number = useConfig(
        PASSWORD_POLICY_CONFIG,
        'reportingUser.minLength',
        DEFAULT_PASSWORD_MIN_LENGTH
    )

    const minLength: number = useMemo(
        (): number => (isAdmin ? minPasswordLengthAdmin : minPasswordLengthReporting),
        [isAdmin, minPasswordLengthAdmin, minPasswordLengthReporting]
    )

    const submit = (data: PasswordChangeFormType): void => {
        setIsSubmitting(true)
        getSession()
            .then((sessionResponse: Nullable<SessionAPIResponse>): void => {
                if (sessionResponse?.Nonce) {
                    if (isStringNotEmpty(accountResetID)) {
                        requestJSON<
                            undefined,
                            {
                                newPassword: string
                                accountResetID: string
                            }
                        >(RESET_PASSWORD_URL, {
                            method: RESTMethods.POST,
                            body: {
                                accountResetID,
                                newPassword: encrypt(data?.newPassword, sessionResponse.Nonce)
                            }
                        })
                            .then((): void => {
                                if (isMounted.current) {
                                    setIsPasswordResetSuccess(true)
                                }
                            })
                            .catch((e: Error): void => {
                                if (isMounted.current) {
                                    setIsSubmitting(false)
                                    displayError(e)
                                }
                            })
                    } else {
                        requestJSON<APIData<{ RedirectThePageTo: string }>>(changeCurrentUserPasswordURL(), {
                            method: RESTMethods.POST,
                            body: {
                                oldPassword: encrypt(data?.oldPassword, sessionResponse.Nonce),
                                newPassword: encrypt(data?.newPassword, sessionResponse.Nonce)
                            }
                        })
                            .then((response: Nullable<APIData<{ RedirectThePageTo: string }>>): void => {
                                if (isMounted.current) {
                                    if (isStringNotEmpty(response?.Data?.RedirectThePageTo)) {
                                        const redirectTo: string = response.Data.RedirectThePageTo?.startsWith(CMD)
                                            ? response.Data.RedirectThePageTo
                                            : `${CMD}${response.Data.RedirectThePageTo}`
                                        const routeToRedirectTo: string = SERVLET_COMMANDS_TO_ROUTE[redirectTo]

                                        if (isStringNotEmpty(routeToRedirectTo)) {
                                            navigate(routeToRedirectTo, {
                                                replace: true,
                                                state: { from: location }
                                            })
                                        } else {
                                            redirectToServletWithBranding(
                                                redirectTo,
                                                branding?.URLMapping ?? APIElements.AKIXI_2019
                                            )
                                        }
                                    } else {
                                        performAuthOutflowRouting(navigate, location, undefined)
                                    }
                                }
                            })
                            .catch((e: Error): void => {
                                if (isMounted.current) {
                                    setIsSubmitting(false)
                                    displayError(e)
                                }
                            })
                    }
                }
            })
            .catch(displayError)
    }

    useEffect((): void => {
        setAccountResetID(getQueryParam('AccountResetID'))
    }, [])

    return (
        <div className={`router-change-password ${isSubmitting ? 'rcp-is-submitting' : EMPTY_STRING}`}>
            {isStringNotEmpty(accountResetID) ? (
                <>
                    <h1 className='pc-title'>{language.get(PasswordChangeLanguage.PasswordReset_Title)}</h1>
                    {isVarNotEmpty(minLength) && !isPasswordResetSuccess ? (
                        <h2 className='pc-sub-title'>
                            {language.getInterpolated(PasswordChangeLanguage.PasswordReset_SubTitle, minLength)}
                        </h2>
                    ) : (
                        <></>
                    )}
                </>
            ) : (
                <>
                    <h1 className='pc-title'>{language.get(PasswordChangeLanguage.PasswordChange_Title)}</h1>
                    {isVarNotEmpty(minLength) && !isSubmitting ? (
                        <h2 className='pc-sub-title'>
                            {language.getInterpolated(PasswordChangeLanguage.PasswordChange_SubTitle, minLength)}
                        </h2>
                    ) : (
                        <></>
                    )}
                </>
            )}
            {!isPasswordResetSuccess ? (
                <>
                    {isSubmitting ? (
                        <Loader />
                    ) : (
                        <FormProvider {...methods}>
                            <form
                                className='pc-form-container'
                                /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
                                onSubmit={methods.handleSubmit(submit)}
                                onKeyDown={(e: React.KeyboardEvent): void => {
                                    if (e?.key === 'Enter') {
                                        methods.handleSubmit(submit)()
                                    }
                                }}
                                noValidate
                            >
                                <PasswordChangeForm accountResetIDState={accountResetIDState} />
                                <FooterActions
                                    isSubmitEnabled={methods.formState.isDirty && methods.formState.isValid}
                                />
                            </form>
                        </FormProvider>
                    )}
                </>
            ) : (
                <>
                    <div className='pc-reset-success'>
                        {LanguageUtils.formatLanguageEntry(language.get(PasswordChangeLanguage.PasswordReset_Success))}
                    </div>
                    <div className='pc-footer-actions'>
                        <RoundedButton
                            className='pc-continue'
                            type='button'
                            onClick={(): void =>
                                navigate(AUTH_ROUTE_PATH, {
                                    replace: true,
                                    state: { from: location }
                                })
                            }
                        >
                            {language.get(PasswordChangeLanguage.PasswordChange_Actions_Continue)}
                        </RoundedButton>
                    </div>
                </>
            )}
        </div>
    )
}
